import { makeStyles, mergeClasses, Radio, RadioGroup, RadioGroupOnChangeData } from '@fluentui/react-components';
import * as React from 'react';
import { Field } from 'react-final-form';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { OperatingSystemFamily } from '../../../constants/browser';
import { getLinkForOS } from '../../../constants/windows-store-links';
import { useStackWithFullWidthItemStyles } from '../../../themes/styles/flexbox-styles';
import { getOperatingSystemFamily } from '../../../utilities/browser';
import { getFormattedMessageWithLinkValues } from '../../common/formatted-link-message';
import { UserSettingsFormFieldName } from '../models';

const messages = defineMessages({
    useSingleMonitorText: {
        id: 'SettingsFormFieldGroup_UseSingleMonitor_Text',
        defaultMessage: 'Use a single monitor',
        description: 'Label for choice group option "Use a single monitor"',
    },
    useMultiMonitorText: {
        id: 'SettingsFormFieldGroup_UseMultiMonitor_Text',
        defaultMessage: 'Use multiple monitors',
        description: 'Label for choice group option "Use a multiple monitors"',
    },
});

const getOsFormattedMessage: () => JSX.Element = () => {
    const osType = getOperatingSystemFamily();
    const linkToDownloadWindowsApp = getLinkForOS(osType);
    const linkToDownloadWindowsAppElement = getFormattedMessageWithLinkValues(linkToDownloadWindowsApp);
    switch (osType) {
        case OperatingSystemFamily.Windows:
            return (
                <FormattedMessage
                    id="WindowsApp_WindowsConnectionPreferenceDescription_Text"
                    defaultMessage="This preference will apply to all Windows App connections, and can be changed at any time in settings. If the Windows App is not installed, <Link>get it from the Microsoft Store</Link>."
                    description="Label text for the Windows app connection preference description. <Link> and </Link> should not be localized, but text between them should."
                    values={linkToDownloadWindowsAppElement}
                />
            );
        case OperatingSystemFamily.MacOS:
        case OperatingSystemFamily.IOS:
            return (
                <FormattedMessage
                    id="WindowsApp_MacConnectionPreferenceDescription_Text"
                    defaultMessage="This preference will apply to all Windows App connections, and can be changed at any time in settings. If the Windows App is not installed, <Link>get it from the App Store</Link>."
                    description="Label text for the Windows app connection preference description. <Link> and </Link> should not be localized, but text between them should."
                    values={linkToDownloadWindowsAppElement}
                />
            );
        case OperatingSystemFamily.Android:
            return (
                <FormattedMessage
                    id="WindowsApp_AndroidConnectionPreferenceDescription_Text"
                    defaultMessage="This preference will apply to all Windows App connections, and can be changed at any time in settings. If the Windows App is not installed, <Link>get it from the Google Play store</Link>."
                    description="Label text for the Windows app connection preference description. <Link> and </Link> should not be localized, but text between them should."
                    values={linkToDownloadWindowsAppElement}
                />
            );
        default:
            return (
                <FormattedMessage
                    id="WindowsApp_ConnectionPreferenceDescription_Text"
                    defaultMessage="This preference will apply to all Windows App connections, and can be changed at any time in settings."
                    description="Label text for the Windows app connection preference description"
                />
            );
    }
};
const osFormattedMessage = getOsFormattedMessage();

/**
 * Styles
 */

const useFormStyles = makeStyles({
    root: {
        gap: '16px',
    },
});

/**
 * END Styles
 */

export const ConnectViaWindowsAppFormFieldGroup: React.FC = () => {
    // Style hooks
    const stackStyles = useStackWithFullWidthItemStyles();
    const formStyles = useFormStyles();

    // Intl hooks
    const { formatMessage } = useIntl();

    return (
        <div className={mergeClasses(stackStyles.root, formStyles.root)}>
            <div className={stackStyles.item}>
                <Field name={UserSettingsFormFieldName.useMultiMonitor}>
                    {(fieldProps) => {
                        const { input } = fieldProps;
                        const { onChange } = input;

                        const onMultiMonitorChange = React.useCallback(
                            (_event: React.FormEvent<HTMLElement> | undefined, data: RadioGroupOnChangeData) => {
                                const selectedKey = data.value === 'true';
                                onChange(selectedKey);
                            },
                            [onChange]
                        );

                        return (
                            <RadioGroup onChange={onMultiMonitorChange} defaultValue="false">
                                <Radio value="false" label={formatMessage(messages.useSingleMonitorText)} />
                                <Radio value="true" label={formatMessage(messages.useMultiMonitorText)} />
                            </RadioGroup>
                        );
                    }}
                </Field>
            </div>

            <div className={stackStyles.item}>{osFormattedMessage}</div>
        </div>
    );
};
